import { useQuery } from '@tanstack/react-query';
import { getApiUrl, fetchWithCredentials } from '../lib/config';

// ==========================================
// Types
// ==========================================

export interface MenuSize {
  nameAr: string;
  nameEn?: string;
  price: number;
}

export interface MenuVariant {
  id: string;
  nameAr: string;
  nameEn?: string;
  priceModifier: number;
}

export interface MilkOption {
  id: string;
  nameAr: string;
  nameEn?: string;
  priceModifier: number;
}

export interface MenuItem {
  id: string;
  nameAr: string;
  nameEn?: string;
  descriptionAr?: string;
  price: string;
  sizes: MenuSize[];
  variants: MenuVariant[];
  imageUrl?: string;
  isAvailable: boolean;
}

export interface MenuCategory {
  id: string;
  nameAr: string;
  nameEn?: string;
  items: MenuItem[];
}

export interface MenuData {
  menu: MenuCategory[];
  milkOptions: MilkOption[];
}

// ==========================================
// API Functions
// ==========================================

async function fetchMenu(shopId: string): Promise<MenuData> {
  const response = await fetch(getApiUrl(`/api/menu/${shopId}/full`), {
    ...fetchWithCredentials,
  });

  if (!response.ok) {
    throw new Error('Failed to fetch menu');
  }

  const data = await response.json();

  return {
    menu: data.menu || [],
    milkOptions: data.milkOptions || [],
  };
}

// ==========================================
// Hook
// ==========================================

export function useMenuQuery(shopId: string) {
  return useQuery({
    queryKey: ['menu', shopId],
    queryFn: () => fetchMenu(shopId),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    enabled: !!shopId,
  });
}
